import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Cloud, 
  Server, 
  Lock, 
  Database, 
  CheckCircle, 
  ArrowRight,
  FileSearch,
  Users,
  FileCheck,
  Network,
  HardDrive,
  Eye,
  EyeOff,
  Mail,
  Scale,
  Building2
} from 'lucide-react';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';
import config from '../config';

const PrivateAI: React.FC = () => {
  const handleContactClick = () => {
    window.location.href = '/contact';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Navigation Bar */}
      <PublicHeader />

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-primary-100 rounded-full mb-4 sm:mb-6">
            <Shield className="h-8 w-8 sm:h-10 sm:w-10 text-primary-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
            Private AI/LLM Deployment
            <span className="block text-primary-600 mt-1 sm:mt-2">Complete Confidentiality & Data Protection</span>
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
            For organizations handling highly sensitive documents, confidential information, or regulated data, 
            Our app offers private AI deployment. Your AI runs on your infrastructure, ensuring complete 
            confidentiality, data protection, and compliance with security and regulatory requirements.
          </p>
          <button
            onClick={handleContactClick}
            className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold text-lg inline-flex items-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
          >
            Contact Us for Private Deployment
            <ArrowRight className="ml-2 h-5 w-5" />
          </button>
        </div>
      </section>

      {/* Why Private AI Section */}
      <section className="bg-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 px-4">
            <Shield className="h-10 w-10 sm:h-12 sm:w-12 text-primary-600 mx-auto mb-3 sm:mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Why Organizations Choose Private AI</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Protect sensitive documents, maintain confidentiality, and ensure complete 
              data security with self-hosted private AI deployment
            </p>
          </div>
          <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 px-4">
            <div className="bg-gray-50 p-6 rounded-lg">
              <Lock className="h-12 w-12 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Complete Confidentiality</h3>
              <p className="text-gray-600">
                Your documents and data never leave your secure infrastructure. Complete protection 
                of sensitive information with zero risk of data exposure to third parties. Essential for 
                confidential documents and high-stakes business operations.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <FileCheck className="h-12 w-12 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Regulatory Compliance</h3>
              <p className="text-gray-600">
                Meet all regulatory requirements and industry standards. Private AI deployment 
                ensures compliance with data protection obligations and protects sensitive information 
                at the highest level.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <Shield className="h-12 w-12 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Complete Data Control</h3>
              <p className="text-gray-600">
                Full control over where your data resides, who has access, and how it's processed. Your 
                sensitive documents stay on your infrastructure, meeting the strictest security 
                and compliance requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Deployment Models Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="text-center mb-8 sm:mb-12 lg:mb-16 px-4">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Private Deployment Options</h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the level of privacy and control that fits your firm's needs—from private cloud to 
            fully air-gapped on-premises solutions
          </p>
        </div>

        {/* Model 1: Private Cloud with Own LLM */}
        <div className="mb-20">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="md:flex">
              <div className="md:w-1/3 bg-gradient-to-br from-blue-50 to-blue-100 p-8 flex items-center justify-center">
                <div className="text-center">
                  <Cloud className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Private Cloud</h3>
                  <p className="text-sm text-gray-600">Your Own LLM Infrastructure</p>
                </div>
              </div>
              <div className="md:w-2/3 p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Private Cloud with Own LLM</h3>
                  <p className="text-lg text-gray-600 mb-6">
                    Complete privacy and control. Deploy both your application and your own LLM infrastructure 
                    on private cloud servers (AWS, Google Cloud, Azure). Maximum security with full data 
                    isolation—no data ever leaves your infrastructure. Perfect for organizations requiring 
                    maximum data protection and confidentiality.
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Shield className="h-5 w-5 text-blue-600 mr-2" />
                      Complete Privacy
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Application on your private cloud</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Your own LLM model hosted privately</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Zero data transmission to external APIs</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Full control over model training and fine-tuning</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Database className="h-5 w-5 text-blue-600 mr-2" />
                      Infrastructure
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Dedicated GPU servers for LLM inference</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Your own vector databases and storage</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Complete network isolation</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Premium security and compliance</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Best for:</strong> Organizations requiring maximum privacy and data control. Perfect for 
                    companies handling sensitive documents, confidential information, regulated data, 
                    or high-stakes business operations. Ensures complete data protection.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Model 2: On-Premises for Maximum Security */}
        <div className="mb-20">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-red-200">
            <div className="md:flex">
              <div className="md:w-1/3 bg-gradient-to-br from-red-50 to-red-100 p-8 flex items-center justify-center">
                <div className="text-center">
                  <HardDrive className="h-16 w-16 text-red-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">On-Premises</h3>
                  <p className="text-sm text-gray-600">Air-Gapped Infrastructure</p>
                </div>
              </div>
              <div className="md:w-2/3 p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">On-Premises Deployment</h3>
                  <p className="text-lg text-gray-600 mb-6">
                    The ultimate in security and isolation. Everything—application, LLM, documents, and infrastructure—runs 
                    entirely on your premises. Complete air-gapped solution with zero connection to external networks. 
                    Designed for organizations handling the most sensitive documents, classified information, or requiring 
                    the highest levels of security clearance.
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <EyeOff className="h-5 w-5 text-red-600 mr-2" />
                      Total Isolation
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>100% on-premises infrastructure</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Air-gapped from internet</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>No external network connections</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Complete physical and logical isolation</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Building2 className="h-5 w-5 text-red-600 mr-2" />
                      Full Control
                    </h4>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Your own data center or secure facility</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Custom LLM deployment and configuration</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Compliance with highest security standards</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>Dedicated support and maintenance</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-sm text-gray-700">
                    <strong>Best for:</strong> Organizations handling the most sensitive documents, classified information, 
                    or requiring the highest security clearance. Meets the strictest security requirements and 
                    provides complete isolation from the online world. Perfect for firms working with government 
                    clients, defense contractors, or highly confidential matters.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Benefits</h2>
            <p className="text-lg text-gray-600">Why private AI deployment is essential for sensitive document work</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <Lock className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Complete Confidentiality</h3>
              <p className="text-gray-600">
                Complete protection of sensitive information. Your documents and data never leave 
                your secure environment, ensuring confidential information remains protected.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <Scale className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Regulatory Compliance</h3>
              <p className="text-gray-600">
                Meet all regulatory requirements and industry standards. Private deployment ensures 
                compliance with data protection and confidentiality obligations.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <Shield className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Enhanced Security</h3>
              <p className="text-gray-600">
                Enterprise-grade security with complete data control. No data leaves your infrastructure, 
                reducing attack surface and compliance risks.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <Users className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Regulatory Compliance</h3>
              <p className="text-gray-600">
                Meet GDPR, HIPAA, SOC 2, and other regulatory requirements. Perfect for organizations with strict 
                data governance needs or international clients.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <Database className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Data Sovereignty</h3>
              <p className="text-gray-600">
                Maintain complete control over where your data resides. Critical for organizations with data 
                residency requirements or international operations.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <Eye className="h-10 w-10 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Audit & Transparency</h3>
              <p className="text-gray-600">
                Full visibility into AI operations, model behavior, and data processing. Complete audit 
                trails for compliance and security reviews.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ideal For</h2>
          <p className="text-lg text-gray-600">Organizations that require the highest levels of privacy and security</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md text-center border-l-4 border-primary-600">
            <FileSearch className="h-12 w-12 text-primary-600 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Sensitive Projects</h3>
            <p className="text-sm text-gray-600">High-stakes operations requiring maximum confidentiality</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center border-l-4 border-primary-600">
            <Shield className="h-12 w-12 text-primary-600 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Confidential Documents</h3>
            <p className="text-sm text-gray-600">Protecting sensitive information at all costs</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center border-l-4 border-primary-600">
            <FileCheck className="h-12 w-12 text-primary-600 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Confidential Client Information</h3>
            <p className="text-sm text-gray-600">Handling highly sensitive client data and documents</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center border-l-4 border-primary-600">
            <Building2 className="h-12 w-12 text-primary-600 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Government & Defense Clients</h3>
            <p className="text-sm text-gray-600">Working with classified or sensitive government matters</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center border-l-4 border-primary-600">
            <Database className="h-12 w-12 text-primary-600 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Regulatory Compliance</h3>
            <p className="text-sm text-gray-600">Firms with strict data governance requirements</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center border-l-4 border-primary-600">
            <Users className="h-12 w-12 text-primary-600 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Large Enterprises</h3>
            <p className="text-sm text-gray-600">Enterprise firms requiring complete data control</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center border-l-4 border-primary-600">
            <Lock className="h-12 w-12 text-primary-600 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">International Operations</h3>
            <p className="text-sm text-gray-600">Firms with data residency requirements</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center border-l-4 border-primary-600">
            <Scale className="h-12 w-12 text-primary-600 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Compliance-Focused Organizations</h3>
            <p className="text-sm text-gray-600">Organizations prioritizing data protection and regulatory compliance</p>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="bg-gray-50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Standard vs. Private Deployment</h2>
            <p className="text-base sm:text-lg text-gray-600">Compare deployment options for your organization</p>
          </div>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full border-collapse min-w-[600px]">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-left font-semibold text-gray-900 text-xs sm:text-sm">Feature</th>
                    <th className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center font-semibold text-gray-900 text-xs sm:text-sm">Standard<br className="hidden sm:block"/>Deployment</th>
                    <th className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center font-semibold text-gray-900 text-xs sm:text-sm">Private Cloud<br/>(Own LLM)</th>
                    <th className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center font-semibold text-gray-900 text-xs sm:text-sm">On-Premises<br/>(Air-Gapped)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 font-medium text-gray-900 text-xs sm:text-sm">Data Storage</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Cloud Hosted</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Your Private Cloud</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Your Premises</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 font-medium text-gray-900 text-xs sm:text-sm">LLM Location</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Cloud Provider API</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Your Private Cloud</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Your Premises</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 font-medium text-gray-900 text-xs sm:text-sm">Complete Confidentiality</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Standard Protection</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center">
                      <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mx-auto" />
                    </td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center">
                      <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mx-auto" />
                    </td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 font-medium text-gray-900 text-xs sm:text-sm">Data Privacy Level</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">High</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Very High</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Maximum</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 font-medium text-gray-900 text-xs sm:text-sm">Internet Connection</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Required</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Optional</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-600 text-xs sm:text-sm">Not Required</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 font-medium text-gray-900 text-xs sm:text-sm">Compliance Ready</td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center">
                      <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mx-auto" />
                    </td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center">
                      <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mx-auto" />
                    </td>
                    <td className="border border-gray-300 px-3 sm:px-6 py-3 sm:py-4 text-center">
                      <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mx-auto" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-700 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Deploy Private AI?</h2>
          <p className="text-xl text-primary-100 mb-8">
            Let's discuss which private deployment option is right for your firm. Our team will help you 
            choose the perfect solution for your security, compliance, and confidentiality requirements.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={handleContactClick}
              className="bg-white text-primary-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2 h-5 w-5" />
              Contact Us Today
            </button>
            <Link
              to="/contact"
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white/10 transition-colors font-semibold text-lg inline-flex items-center justify-center"
            >
              Schedule a Demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter
        ctaLabel="Request Private Deployment"
        ctaTo="/contact"
      />
    </div>
  );
};

export default PrivateAI;
